/** 运行与回放屏的后端待加三项（#4752）：跑一天触发人留痕、同日重跑指纹、回放对照。
 *
 * 形状以后端为准（`someo_keel/api/paper.py` 的 `_record_trigger`、`someo_keel/paper.py` 的 `reruns`、
 * `someo_keel/api/console_g5.py` 的 `/replay`）。后端承诺必有的字段缺了就抛 `ContractError`，不猜、不补默认值。
 */

import { apiUrl, expectList, expectRecord, optionalString, post, query, requireBoolean, requireNumber, requireString } from "../api/client";
import { ContractError } from "../api/errors";
import type { Rerun } from "../api/types";

const EVENTS = "/paper/events";

/** 一次被受理的「跑一天 / 补跑」：谁、为什么、开关、任务号。 */
export interface RunTrigger {
  ts_ns: number;
  trade_date: string;
  catch_up: boolean;
  dry_run: boolean;
  operator: string;
  reason: string;
  job_id: string;
}

/** `kind=run` 里同源运行（`POST /runs`，E5 #5008）的行带 `run_id`；跑一天 / 补跑的行没有。 */
const isSameSourceRun = (row: Record<string, unknown>): boolean => row["kind"] === "run" && "run_id" in row;

/** 控制事件台账里跑一天 / 补跑的 `kind=run` 行（同源运行的行另由 `parseSameSourceTriggers` 读；其它种类由停机屏等各自读）。 */
export function parseRunTriggers(body: unknown): RunTrigger[] {
  const url = apiUrl(EVENTS);
  return expectList(body, "events", url, "控制事件")
    .filter((row) => row["kind"] === "run" && !isSameSourceRun(row))
    .map((row) => ({
      ts_ns: requireNumber(row, "ts_ns", url),
      trade_date: requireString(row, "trade_date", url),
      catch_up: requireBoolean(row, "catch_up", url),
      dry_run: requireBoolean(row, "dry_run", url),
      operator: requireString(row, "operator", url),
      reason: requireString(row, "reason", url),
      job_id: requireString(row, "job_id", url),
    }));
}

export const readRunTriggers = (): Promise<RunTrigger[]> => query<unknown>(EVENTS, { limit: 200 }).then(parseRunTriggers);

// ---- 同源跑一遍（POST /runs 的触发留痕，E5 #5008）----------------------------

export const SAME_SOURCE_RUN_PATH = "/runs";

/** 「同源跑一遍」表单之外随请求带上的字段：本进程只跑回测（纸上盘归 keel-paper 进程）。 */
export const SAME_SOURCE_RUN_EXTRA = { mode: "回测" } as const;

/** 一次被受理的同源运行：`source=operator` 带操作人与理由；`source=api` 是不带人的调用方（已过令牌鉴权），两项为 null。 */
export interface SameSourceTrigger {
  ts_ns: number;
  run_id: string;
  mode: string;
  snapshot_id: string | null;
  operator: string | null;
  reason: string | null;
  source: string;
}

export function parseSameSourceTriggers(body: unknown): SameSourceTrigger[] {
  const url = apiUrl(EVENTS);
  return expectList(body, "events", url, "控制事件")
    .filter(isSameSourceRun)
    .map((row) => ({
      ts_ns: requireNumber(row, "ts_ns", url),
      run_id: requireString(row, "run_id", url),
      mode: requireString(row, "mode", url),
      snapshot_id: optionalString(row, "snapshot_id"),
      operator: optionalString(row, "operator"),
      reason: optionalString(row, "reason"),
      source: requireString(row, "source", url),
    }));
}

export const readSameSourceTriggers = (): Promise<SameSourceTrigger[]> =>
  query<unknown>(EVENTS, { limit: 200 }).then(parseSameSourceTriggers);

/** 同日重跑一句话结论：只跑过一次 / 历次一致 / 第几次与上一次不同。判等由后端 `same_as_previous` 给，前端不比指纹。 */
export function rerunVerdict(reruns: Rerun[]): string {
  if (reruns.length <= 1) return "该日只跑过一次";
  const changed = reruns.findIndex((item, index) => index > 0 && item.same_as_previous === false);
  if (changed > 0) return `账目指纹不一致：第 ${changed + 1} 次与上一次不同`;
  const unknown = reruns.some((item, index) => index > 0 && item.same_as_previous === null);
  return unknown ? `共 ${reruns.length} 次，有一次无法比对（缺上一次指纹）` : `共 ${reruns.length} 次，账目指纹一致`;
}

// ---- 回放对照（POST /replay · GET /replay/{replay_id}）------------------------

export interface DecisionDiff {
  index: number;
  /** `client_order_id` / `instrument_id` / `side` / `quantity` / `ts_event`；多出或缺少一条为 `order`。 */
  field: string;
  original: unknown;
  replay: unknown;
  client_order_id: string | null;
}

export interface ReplayDay {
  trade_date: string;
  original_run_id: string | null;
  snapshot_id: string | null;
  config_digest: string | null;
  replay_config_digest: string | null;
  original_ledger_digest: string | null;
  replay_ledger_digest: string | null;
  orders: number | null;
  /** 后端判等：true 一致 / false 不一致 / null 回放不了（看 `missing_input`）。 */
  same: boolean | null;
  decision_diffs: DecisionDiff[];
  missing_input: string | null;
}

export type ReplayStatus = "running" | "ok" | "failed";

export interface ReplayResult {
  replay_id: string;
  strategy_id: string;
  from: string;
  to: string;
  operator: string;
  reason: string;
  status: ReplayStatus;
  job_id: string | null;
  error: string | null;
  all_same: boolean | null;
  days: ReplayDay[];
}

function nullableString(row: Record<string, unknown>, key: string, url: string): string | null {
  const v = row[key];
  if (v === null) return null;
  if (typeof v !== "string") throw new ContractError(`字段 \`${key}\` 应为字符串或 null`, url, row);
  return v;
}

function nullableBoolean(row: Record<string, unknown>, key: string, url: string): boolean | null {
  const v = row[key];
  if (v === null) return null;
  if (typeof v !== "boolean") throw new ContractError(`字段 \`${key}\` 应为布尔或 null`, url, row);
  return v;
}

function nullableNumber(row: Record<string, unknown>, key: string, url: string): number | null {
  const v = row[key];
  if (v === null) return null;
  if (typeof v !== "number") throw new ContractError(`字段 \`${key}\` 应为数字或 null`, url, row);
  return v;
}

export function parseReplayResult(body: unknown, replayId: string): ReplayResult {
  const url = apiUrl(`/replay/${encodeURIComponent(replayId)}`);
  const r = expectRecord(body, url, "回放结果");
  const status = requireString(r, "status", url);
  if (status !== "running" && status !== "ok" && status !== "failed") {
    throw new ContractError(`字段 \`status\` 只能是 running / ok / failed，实际是 ${status}`, url, r);
  }
  return {
    replay_id: requireString(r, "replay_id", url),
    strategy_id: requireString(r, "strategy_id", url),
    from: requireString(r, "from", url),
    to: requireString(r, "to", url),
    operator: requireString(r, "operator", url),
    reason: requireString(r, "reason", url),
    status,
    job_id: nullableString(r, "job_id", url),
    error: nullableString(r, "error", url),
    all_same: nullableBoolean(r, "all_same", url),
    days: expectList(r, "days", url, "回放逐日").map((d) => ({
      trade_date: requireString(d, "trade_date", url),
      original_run_id: nullableString(d, "original_run_id", url),
      snapshot_id: nullableString(d, "snapshot_id", url),
      config_digest: nullableString(d, "config_digest", url),
      replay_config_digest: nullableString(d, "replay_config_digest", url),
      original_ledger_digest: nullableString(d, "original_ledger_digest", url),
      replay_ledger_digest: nullableString(d, "replay_ledger_digest", url),
      orders: nullableNumber(d, "orders", url),
      same: nullableBoolean(d, "same", url),
      missing_input: nullableString(d, "missing_input", url),
      decision_diffs: expectList(d, "decision_diffs", url, "决策差异").map((x) => ({
        index: requireNumber(x, "index", url),
        field: requireString(x, "field", url),
        original: x["original"],
        replay: x["replay"],
        client_order_id: nullableString(x, "client_order_id", url),
      })),
    })),
  };
}

/** 一天的结论文案：「回放不了」与「不一致」分开说（屏规格空态节）。 */
export function dayVerdict(day: ReplayDay): string {
  if (day.missing_input) return `原运行的输入已缺失（缺 ${day.missing_input}），无法回放`;
  if (day.same === true) return "一致";
  if (day.same === false) return "不一致";
  return "无从判定";
}

export interface ReplayRequest {
  strategy_id: string;
  from: string;
  to: string;
  operator: string;
  reason: string;
}

export function parseReplayStart(body: unknown): { job_id: string; replay_id: string } {
  const url = apiUrl("/replay");
  const r = expectRecord(body, url, "回放受理");
  return { job_id: requireString(r, "job_id", url), replay_id: requireString(r, "replay_id", url) };
}

export const startReplay = (request: ReplayRequest) => post<unknown>("/replay", request).then(parseReplayStart);
export const readReplay = (replayId: string): Promise<ReplayResult> =>
  query<unknown>(`/replay/${encodeURIComponent(replayId)}`).then((body) => parseReplayResult(body, replayId));
